const LOCATION_CHECK_KEY = 'location_check';

/**
 * Checks if the user agent is from the Shopify admin context.
 * Useful for preventing the theme preview thumbnail from displaying the localisation modal.
 *
 * @returns {boolean}
 */
function isShopifyAdminContext() {
  if (navigator?.userAgent?.includes('Shopify')) {
    return true;
  }
  return false;
}

/**
 * Checks if the store's location is different from the user's detected country.
 * If it is, it shows the modal and gives the user the option to change their location.
 * If the detected country is the same as the store's location, it does nothing.
 *
 * @returns {void}
 */
async function checkUserLocation() {
  if (sessionStorage.getItem(LOCATION_CHECK_KEY)) {
    return;
  }

  if (window.Shopify.designMode || isShopifyAdminContext()) {
    return;
  }

  try {
    const response = await fetch(
      window.Shopify.routes.root +
        'browsing_context_suggestions.json' +
        '?country[enabled]=true' +
        `&country[exclude]=${window.Shopify.country}` +
        '&language[enabled]=true' +
        `&language[exclude]=${window.Shopify.language}`
    );

    const suggestions = await response.json();

    sessionStorage.setItem(LOCATION_CHECK_KEY, 'true');

    const currentCountry = window.Shopify.country;
    const detectedCountry = suggestions?.detected_values?.country?.handle;

    if (detectedCountry === currentCountry) {
      return;
    }

    if (detectedCountry) {
      const dialog = document.querySelector('.localisation-modal');
      dialog.show();
    }
  } catch (error) {
    console.error('Location check failed:', error);
  }
}

// Run the check on page load
document.addEventListener('DOMContentLoaded', () => {
  checkUserLocation();
});

class LocalizationFormInput extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector(
        'input[name="language_code"], input[name="country_code"]'
      ),
      button: this.querySelector('button'),
      panel: this.querySelector('ul'),
    };
    this.elements.button.addEventListener(
      'click',
      this.openSelector.bind(this)
    );
    this.elements.button.addEventListener(
      'focusout',
      this.closeSelector.bind(this)
    );
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach((item) =>
      item.addEventListener('click', this.onItemClick.bind(this))
    );

    // Outside click listener
    document.addEventListener('click', (e) => {
      if (
        e.target !== this.elements.button &&
        !this.elements.panel.contains(e.target)
      ) {
        this.hidePanel();
      }
    });
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    // Stop event from bubbling to modal
    event.stopPropagation();
    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;

    const textElement = this.elements.button.querySelector(
      '.disclosure__button-text'
    );

    if (textElement) {
      const countryName = event.currentTarget.textContent.split('(')[0].trim();
      textElement.textContent = countryName;
    }

    this.hidePanel();
  }

  openSelector() {
    this.elements.panel.style.maxHeight = `${this.getMaximumPanelHeight()}px`;

    this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute(
      'aria-expanded',
      (
        this.elements.button.getAttribute('aria-expanded') === 'false'
      ).toString()
    );
  }

  closeSelector(event) {
    const shouldClose =
      event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  getMaximumPanelHeight() {
    const paddingBottom = 130;

    return (
      window.innerHeight -
      this.elements.button.getBoundingClientRect().bottom -
      paddingBottom
    );
  }
}

if (!customElements.get('localization-form-input')) {
  customElements.define('localization-form-input', LocalizationFormInput);
}
